import { FC, useState } from 'react';

import { Grid, useTheme, Skeleton, Theme, Typography } from '@mui/material';

import {
	Maybe,
	Asset,
	BffFacility,
	Facility,
	GoogleMapTemplateNoResultsFoundMessage,
	ContentTypeRichTextContent,
	Button,
	Header,
} from '@/types/generated';
import TAGSvgIcon from '@/components/TAGSvgIcon';
import ContentTypeRichText from '@/components/ContentTypeRichText';
import { HeadingTag, SchedulingSystemType } from '@/types';
import getHeadingVariant from '@/utils/resolveContentfulHeadingVariant';

import TAGTabs from '../../TAGTabs';
import { IFacilitiesData } from '../GoogleMap';
import { RequestStatus } from '../GoogleMap.helpers';
import { getMapAndLocationsComponentStyles } from '../GoogleMap.styles';

import LocationCard from './LocationCard/LocationCard';
import {
	createMobileLocationCardStyles,
	mobileLocationCardsContainer,
	getHeadingStyles,
	getMapContainerStyles,
	getLocationsCardContainerStyles,
	noResultsFoundMessageStyles,
	noResultsFoundIconStyles,
} from './StandardGoogleMap.styles';
import LocationSearch from './LocationSearch/LocationSearch';
import { IGoogleMapsSection } from './GoogleMapSection/GoogleMapSection';
import GoogleMapSection from './GoogleMapSection';

interface INearbyLocationsCards {
	nearByFacilities: BffFacility[];
	currentFacilityId: string;
	defaultImage?: Maybe<Asset>;
	selectedImage?: Maybe<Asset>;
	googleMapsBusinessQuery?: Maybe<string>;
	facilityBasePath: string;
	isSmallScreen: boolean;
	holidayHoursMessageText?: Maybe<string>;
	noResultsFoundMessage?: Maybe<GoogleMapTemplateNoResultsFoundMessage>;
	pilotLocations: Set<string>;
	isHorizontalScrolling?: boolean;
	facilityBadgeLabel?: Maybe<string>;
	facilityButtonLabel?: Maybe<string>;
	scheduleButton?: Maybe<Button>;
	officeDetailsButton?: Maybe<Button>;
	isLocationCardsInHeader?: boolean;
	setAsOfficeButtonText?: Maybe<string>;
	showSetOfficeButtonInGoogleMapsCards?: boolean;
}

const NearbyLocationsCards: FC<INearbyLocationsCards> = ({
	nearByFacilities,
	currentFacilityId,
	defaultImage,
	selectedImage,
	googleMapsBusinessQuery,
	facilityBasePath,
	isSmallScreen,
	holidayHoursMessageText,
	noResultsFoundMessage,
	pilotLocations,
	isHorizontalScrolling = false,
	facilityBadgeLabel,
	facilityButtonLabel,
	scheduleButton,
	officeDetailsButton,
	isLocationCardsInHeader = false,
	setAsOfficeButtonText,
	showSetOfficeButtonInGoogleMapsCards,
}) => {
	return (
		<>
			{nearByFacilities &&
				nearByFacilities.length > 0 &&
				nearByFacilities.map((facility: BffFacility, index: number) => {
					const { scheduleAppointmentURL, schedulingSystem, revspringLocationValue = '' } = facility;
					const newFacility = facility as Facility;
					newFacility.scheduling = {
						...newFacility.scheduling,
						schedulingUrl: scheduleAppointmentURL,
					};
					const locationScheduling = {
						schedulingSystem: schedulingSystem as SchedulingSystemType,
					};
					const isInPilot = pilotLocations.has(revspringLocationValue || '');
					return (
						<LocationCard
							key={newFacility.code}
							index={index}
							location={newFacility}
							isSmallScreen={isSmallScreen}
							distance={facility.location?.distance as number}
							defaultImage={defaultImage}
							selectedImage={selectedImage}
							googleMapsBusinessQuery={googleMapsBusinessQuery}
							facilityBasePath={facilityBasePath}
							isFromGoogleMaps
							isCurrentSelectedFacility={facility.code === currentFacilityId}
							holidayHoursMessageText={holidayHoursMessageText || ''}
							locationScheduling={isInPilot ? locationScheduling : undefined}
							hideCardImage
							isHorizontalScrolling={isHorizontalScrolling}
							facilityBadgeLabel={facilityBadgeLabel}
							facilityButtonLabel={facilityButtonLabel}
							scheduleButton={scheduleButton}
							officeDetailsButton={officeDetailsButton}
							isLocationCardsInHeader={isLocationCardsInHeader}
							setAsOfficeButtonText={setAsOfficeButtonText}
							showSetOfficeButtonInGoogleMapsCards={showSetOfficeButtonInGoogleMapsCards}
						/>
					);
				})}
			{nearByFacilities && nearByFacilities.length === 0 && (
				<Grid
					data-test-id="google_maps_no_results_section"
					container
					item
					xs={12}
					sx={noResultsFoundMessageStyles}
				>
					<Grid item xs={1} sx={noResultsFoundIconStyles}>
						<TAGSvgIcon icon={'CircleInfo'} size={24} />
					</Grid>
					<Grid item xs={11}>
						{noResultsFoundMessage?.json && (
							<ContentTypeRichText
								content={noResultsFoundMessage as ContentTypeRichTextContent}
								dataTestId="google_maps_no_results_section_richtext"
								contentMaxWidth="100%"
								disableGutters={true}
								disablePaddingOn={['Top', 'Bottom']}
								backgroundColor="transparent"
								docProps={{ variant: 'notification', color: 'text.secondary' }}
							/>
						)}
					</Grid>
				</Grid>
			)}
		</>
	);
};
interface ILoadingSkeleton {
	theme: Theme;
	isSmallScreen: boolean;
	fullWidth: boolean;
	isLocationCardsInHeader: boolean;
}

function LoadingSkeleton({ theme, isSmallScreen, fullWidth, isLocationCardsInHeader }: Readonly<ILoadingSkeleton>) {
	return (
		<Grid
			data-test-id="google_maps_skeleton_grid_container"
			container
			direction={isSmallScreen ? 'column' : 'row'}
			sx={getMapAndLocationsComponentStyles(theme, isSmallScreen, fullWidth)}
		>
			<Grid
				data-test-id="google_maps_skeleton_location_cards_container"
				item
				container
				rowGap={3}
				md={isLocationCardsInHeader ? 12 : 4}
				sx={getLocationsCardContainerStyles(theme, fullWidth, isLocationCardsInHeader, true)}
			>
				{' '}
				{isSmallScreen ? (
					<>
						<Skeleton variant="rounded" height="6rem" />
						<Skeleton variant="rounded" height="16rem" />
						<Skeleton variant="rounded" height="16rem" />
						{isLocationCardsInHeader && <Skeleton variant="rounded" height="16rem" />}
					</>
				) : (
					<>
						<Skeleton variant="rounded" height="3rem" />
						<Skeleton variant="rounded" height="5rem" />
						<Skeleton variant="rounded" height="12rem" />
						<Skeleton variant="rounded" height="12rem" />
						<Skeleton variant="rounded" height="12rem" />
						<Skeleton variant="rounded" height="12rem" />
					</>
				)}
			</Grid>
			<Grid item md={8} data-test-id="google_maps_skeleton_map_section" sx={getMapContainerStyles(theme)}>
				<Skeleton variant="rounded" height={'100%'} width={'100%'} />
			</Grid>
		</Grid>
	);
}
export interface IStandardGoogleMap {
	isSmallScreen?: boolean;
	fullWidth: boolean;
	isLocationCardsInHeader: boolean;
	shouldShowSearch: boolean;
	listTabIsHorizontalScrolling: boolean;
	googleMapsSectionProps: IGoogleMapsSection;
	nearByLocationsCardsProps: INearbyLocationsCards;
	facilitiesData: IFacilitiesData;
	searchValue: string;
	setSearchValue: (value: string) => void;
	locationBarFindOfficeUrl?: Header['locationBarFindOfficeUrl'];
	updateDataFromLocationSearch: (facilitiesByAddress: BffFacility[]) => void;
	handleRevertToCurrentFacility: () => void;
	searchTitle?: Maybe<string>;
	searchTitleHtag?: Maybe<HeadingTag>;
	useGoogleSearch?: boolean;
}

export default function StandardGoogleMap({
	searchValue,
	setSearchValue,
	searchTitle,
	searchTitleHtag = 'h1',
	googleMapsSectionProps,
	nearByLocationsCardsProps,
	isSmallScreen = false,
	fullWidth,
	isLocationCardsInHeader,
	shouldShowSearch,
	listTabIsHorizontalScrolling,
	facilitiesData,
	locationBarFindOfficeUrl,
	updateDataFromLocationSearch,
	handleRevertToCurrentFacility,
	useGoogleSearch,
}: Readonly<IStandardGoogleMap>) {
	const theme = useTheme();
	const [forceZoomOut, setForceZoomOut] = useState(false);

	if (facilitiesData.reqStatus !== RequestStatus.Success) {
		return (
			<LoadingSkeleton
				theme={theme}
				fullWidth={fullWidth}
				isSmallScreen={isSmallScreen}
				isLocationCardsInHeader={isLocationCardsInHeader}
			/>
		);
	}

	const tabsForMobileView = [
		{
			label: 'List',
			href: '',
			value: 'list',
			content: (
				<Grid
					container
					direction={listTabIsHorizontalScrolling ? 'row' : 'column'}
					data-test-id={'mobile_listTab_location_cards_container'}
					sx={mobileLocationCardsContainer(listTabIsHorizontalScrolling)}
				>
					<NearbyLocationsCards
						{...nearByLocationsCardsProps}
						isHorizontalScrolling={listTabIsHorizontalScrolling}
					/>
				</Grid>
			),
		},
		{
			label: 'Map',
			href: '',
			value: 'map',
			content: (
				<>
					<GoogleMapSection {...googleMapsSectionProps} forceZoomOut={forceZoomOut} />
					<Grid
						container
						direction={'row'}
						data-test-id={'mobile_mapsTab_location_cards_container'}
						sx={mobileLocationCardsContainer(true)}
					>
						<NearbyLocationsCards {...nearByLocationsCardsProps} isHorizontalScrolling={true} />
					</Grid>
				</>
			),
		},
	];

	return (
		<>
			{!shouldShowSearch && (
				<Grid container direction="column" justifyContent="center" alignItems="center" spacing={2}>
					<Grid item xs={12}>
						<Typography
							data-test-id="google_map_title"
							variant={getHeadingVariant(searchTitleHtag as HeadingTag)}
							component={
								searchTitleHtag === 'normal' ? 'p' : searchTitleHtag === null ? 'h1' : searchTitleHtag
							}
							color="primary"
							sx={{
								...getHeadingStyles(theme, 'header2'),
								margin: '20px 0',
							}}
						>
							{searchTitle}
						</Typography>
					</Grid>
				</Grid>
			)}
			<Grid
				data-test-id="google_maps_grid_container"
				container
				direction={isSmallScreen ? 'column' : 'row'}
				sx={getMapAndLocationsComponentStyles(theme, fullWidth, isLocationCardsInHeader)}
			>
				{/* Locations cards container item */}
				<Grid
					data-test-id="google_maps_location_cards_container"
					item
					container
					rowGap={3}
					md={isLocationCardsInHeader ? 12 : 4}
					sx={getLocationsCardContainerStyles(theme, fullWidth, isLocationCardsInHeader, false)}
				>
					{/* Location Search */}
					{shouldShowSearch && (
						<LocationSearch
							title={searchTitle}
							titleHtag={searchTitleHtag}
							updateDataFromLocationSearch={updateDataFromLocationSearch}
							revertToCurrentFacility={handleRevertToCurrentFacility}
							searchQueryValueFromUrl={searchValue}
							onSearchQueryChange={setSearchValue}
							isLocationCardsInHeader={isLocationCardsInHeader}
							locationBarFindOfficeUrl={locationBarFindOfficeUrl}
							useGoogleSearch={useGoogleSearch}
							googleMapApiKey={googleMapsSectionProps?.googleMapApiKey}
							setForceZoomOut={setForceZoomOut}
						/>
					)}
					{/* Location card */}
					{!isSmallScreen && (
						<NearbyLocationsCards
							{...nearByLocationsCardsProps}
							isLocationCardsInHeader={isLocationCardsInHeader}
						/>
					)}
				</Grid>
				{/* maps item */}
				{!isSmallScreen && !isLocationCardsInHeader && (
					<GoogleMapSection {...googleMapsSectionProps} forceZoomOut={forceZoomOut} />
				)}
				{isSmallScreen && (
					<Grid
						item
						data-test-id="google_maps_location_cards_container_mobile"
						sx={createMobileLocationCardStyles(listTabIsHorizontalScrolling, isLocationCardsInHeader)}
					>
						<TAGTabs tabs={tabsForMobileView} dataTestId="google_maps_location_cards_tabs_mobile" />
					</Grid>
				)}
			</Grid>
		</>
	);
}
